import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { TruckDetailListing } from "../../models/TruckDetailListing";
import { useLocation } from "react-router-dom";
import { appRoutes } from "../../constants/appRoutes";
interface truckDetailProviderProps {
  children: ReactNode;
}

interface truckDetailContextType {
  truckDetails: TruckDetailListing[];
  setTruckDetails: (val: TruckDetailListing[]) => void;
  resetTruckDetails: () => void;
  appendTruckDetail: (truckDetails: TruckDetailListing[]) => void;
  truckFormType: string;
  setTruckFormType: (truckAddFormType: string) => void;
  removeTruckDetail: (id: number) => void;
  getTruckDetail: (uniqKey: string) => truckTruck;
  updateTruckDetails: (
    currentRecord?: TruckDetailListing,
    updateTruckDetails: Partial<TruckDetailListing>
  ) => void;
}

const TruckDetailContext = createContext<truckDetailContextType | undefined>(
  undefined
);

export const useTruckDetailContext = (): truckDetailContextType => {
  const context = useContext(TruckDetailContext);
  if (!context) {
    throw new Error(
      "useTruckDetailContext must be used within a truckDetailProvider"
    );
  }
  return context;
};

export const TruckDetailProvider: React.FC<truckDetailProviderProps> = ({
  children,
}) => {
  const [truckFormType, setTruckFormType] = useState("");
  const [truckDetails, setTruckDetails] = useState<TruckDetailListing[]>([]);
  const appendTruckDetail = (truckDetails: TruckDetailListing) => {
    setTruckDetails((prevTruckDetail) => [...prevTruckDetail, ...truckDetails]);
  };

  const resetTruckDetails = () => {
    setTruckDetails([]);
  };

  const getTruckDetail = (uniqKey: number): TruckDetailListing => {
    return truckDetails.find((truck) => truck.uniqKey === uniqKey);
  };

  const compareTrucks = (
    truck1: TruckDetailListing,
    truck2: TruckDetailListing
  ) => {
    if (
      truck1.materialName !== truck2.materialName ||
      truck1.pickUpAddress !== truck2.pickUpAddress ||
      truck1.truckingPriceType !== truck2.truckingPriceType ||
      truck1.truckingCost !== truck2.truckingCost ||
      truck1.assigned !== truck2.assigned ||
      truck1.type !== truck2.type ||
      truck1.truckingPrice !== truck2.truckingPrice ||
      truck1.truckingCostType !== truck2.truckingCostType ||
      truck1.id !== truck2.id ||
      truck1.pickupLocation !== truck2.pickupLocation ||
      truck1.milesDuration !== truck2.milesDuration ||
      truck1.materialTruckName !== truck2.materialTruckName
    ) {
      return false;
    }
    // Comparing nested objects
    if (
      truck1.duration.value !== truck2.duration.value ||
      truck1.duration.text !== truck2.duration.text ||
      truck1.pickUpLatLng.lat !== truck2.pickUpLatLng.lat ||
      truck1.pickUpLatLng.lng !== truck2.pickUpLatLng.lng ||
      truck1.milesData.value !== truck2.milesData.value ||
      truck1.milesData.text !== truck2.milesData.text
    ) {
      return false;
    }

    // Compare uniqKey if it exists
    if (truck1.uniqKey !== undefined && truck2.uniqKey !== undefined) {
      if (truck1.uniqKey !== truck2.uniqKey) {
        return false;
      }
    }

    return true;
  };

  // Add this new comparison function in for edit alone as we may need to handle edit difference provider
  const compareTrucksForUpdate = (
    truck1: TruckDetailListing,
    truck2: TruckDetailListing
  ) => {
    return (
      Number(truck1.truckingCost) === Number(truck2.truckingCost) &&
      Number(truck1.truckingPrice) === Number(truck2.truckingPrice) &&
      truck1.truckingCostType === truck2.truckingCostType &&
      truck1.truckingPriceType === truck2.truckingPriceType &&
      truck1.pickupLocation === truck2.pickUpAddress &&
      truck1.deliveryLocation === truck2.deleiveryAddress && // Note different property names
      truck1.milesDuration === truck2.milesAndDistance && // Note different property names
      JSON.stringify(truck1.truckType) === JSON.stringify(truck2.truckType)
    );
  };
  // Usage in your remove function
  const removeTruckDetail = (row?: TruckDetailListing) => {
    if (!row) return;
    setTruckDetails((prev) =>
      prev?.filter((truck) => !compareTrucks(truck, row))
    );
  };
  // Then update your updateTruckDetails function to use this new comparison
  const updateTruckDetails = (
    truckToUpdate?: TruckDetailListing,
    updatedDetails?: Partial<TruckDetailListing>
  ) => {
    setTruckDetails((prevTrucks) =>
      prevTrucks?.map((truck) =>
        compareTrucksForUpdate(truck, truckToUpdate)
          ? { ...truck, ...updatedDetails }
          : truck
      )
    );
  };
  const location = useLocation();

  useEffect(() => {
    if (
      location &&
      !location?.pathname?.startsWith(appRoutes.ADD_TRUCKING_MATERIAL) &&
      !location?.pathname?.startsWith(appRoutes.UPDATE_TRUCKING_MATERIAL) &&
      !location?.pathname?.startsWith(appRoutes.VIEW_TRUCKING_MATERIAL)
    ) {
      resetTruckDetails();
    }
  }, [location?.pathname]);

  return (
    <TruckDetailContext.Provider
      value={{
        truckDetails,
        truckFormType,
        setTruckFormType,
        setTruckDetails,
        resetTruckDetails,
        appendTruckDetail,
        removeTruckDetail,
        getTruckDetail,
        removeTruckDetail,
        updateTruckDetails,
      }}
    >
      {children}
    </TruckDetailContext.Provider>
  );
};
